import useIsCompactViewport from '@Guidelines/Component/FilterPanel/Hooks/useIsCompactViewport';
import requestDocumentPanel from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Helpers/requestDocumentPanel';
import DocumentViewerContext from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/DocumentViewerContext';
import buildViewerFieldIndex from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/buildViewerFieldIndex';
import type { ShownChange } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/diffShownFields';
import findAdjacentPendingField from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/findAdjacentPendingField';
import findViewerItemPath from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/findViewerItemPath';
import findViewerOccurrence from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/findViewerOccurrence';
import isSamePanelTarget from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/isSamePanelTarget';
import parseInstancePositions from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/parseInstancePositions';
import resolveFitZoom from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/resolveFitZoom';
import resolvePanelTarget from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/Helpers/resolvePanelTarget';
import type { DocumentViewerContextValue } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/useDocumentViewer';
import type { ViewerListActionHandler } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/ViewerListAction';
import documentPanelSignal, {
	takeDocumentPanelOpener,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/documentPanelSignal';
import editingFieldSignal from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/editingFieldSignal';
import { setPendingDiscard } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/pendingDiscardSignal';
import type DocumentDetailView from '@UI/Store/Documents/Views/DocumentDetailView';
import type DocumentFieldView from '@UI/Store/Documents/Views/DocumentFieldView';
import { useSignalValue } from '@verclam/pulsar';
import type {
	ViewerAddItemEvent,
	ViewerClickEvent,
	ViewerEditItemEvent,
	ViewerFieldInfo,
	ViewerMoveItemEvent,
	ViewerRemoveItemEvent,
} from '@verclam/docx-kit-editor';
import type { DocxViewerHandle } from '@verclam/docx-kit-react';
import * as React from 'react';

const MIN_ZOOM = 0.35;
const MAX_ZOOM_DESKTOP = 2;
const MAX_ZOOM_PHONE = 2.5;
const READABLE_ZOOM = 1;

type Props = {
	readonly document: DocumentDetailView;
	/** `true` when the template cannot be read: the document can be neither shown nor downloaded. */
	readonly isTemplateUnreadable: boolean;
	readonly children: React.ReactNode;
};

/**
 * State of the document screen shared by the viewer, the progress strip, the phone bar and the sidebar:
 * the viewer handle, the zoom, the viewer ↔ Dixit lookup, and the guarded navigation between fields.
 * The open panel itself lives in `documentPanelSignal`; this provider keeps the viewer's selection in
 * step with it.
 */
const DocumentViewerProvider: React.FC<Props> = ({ document, isTemplateUnreadable, children }) => {
	const isCompact = useIsCompactViewport();
	const handleRef = React.useRef<DocxViewerHandle | null>(null);
	const listActionHandlerRef = React.useRef<ViewerListActionHandler | null>(null);
	const revealRequestRef = React.useRef<string | null>(null);
	const afterZoomRef = React.useRef<{ readonly zoom: number; readonly run: () => void } | null>(null);
	const lastDocumentRef = React.useRef<DocumentDetailView | null>(null);
	const panelTarget = useSignalValue(documentPanelSignal);

	const [viewportWidth, setViewportWidth] = React.useState(0);
	const [manualZoom, setManualZoom] = React.useState<number | null>(null);
	const [isViewerReady, setIsViewerReady] = React.useState(false);
	const [viewerRevision, setViewerRevision] = React.useState(0);

	const maxZoom = isCompact ? MAX_ZOOM_PHONE : MAX_ZOOM_DESKTOP;
	const fitZoom = React.useMemo(() => resolveFitZoom(viewportWidth, isCompact), [viewportWidth, isCompact]);
	const zoom = Math.min(maxZoom, Math.max(MIN_ZOOM, manualZoom ?? fitZoom));
	const isFitted = manualZoom === null;

	const fieldIndex = React.useMemo(() => buildViewerFieldIndex(document), [document]);

	const setZoom = React.useCallback(
		(next: number) => {
			setManualZoom(Math.round(Math.min(maxZoom, Math.max(MIN_ZOOM, next)) * 100) / 100);
		},
		[maxZoom],
	);

	const fitToWidth = React.useCallback(() => {
		setManualZoom(null);
	}, []);

	const reportViewportWidth = React.useCallback((width: number) => {
		setViewportWidth(width);
	}, []);

	const reportViewerReady = React.useCallback(() => {
		setIsViewerReady(true);
		setViewerRevision((revision) => revision + 1);
	}, []);

	// The viewer applies a new `data` in a layout effect of its own, which runs before this effect: the
	// fields it reports are those of the new document from here on.
	React.useEffect(() => {
		if (lastDocumentRef.current === document) {
			return;
		}

		lastDocumentRef.current = document;
		setViewerRevision((revision) => revision + 1);
	}, [document]);

	// Runs a reveal that had to wait for a zoom change, once the viewer has applied it.
	React.useEffect(() => {
		const pending = afterZoomRef.current;

		if (pending === null || pending.zoom !== zoom) {
			return undefined;
		}

		afterZoomRef.current = null;
		let secondFrame = 0;
		const firstFrame = requestAnimationFrame(() => {
			secondFrame = requestAnimationFrame(pending.run);
		});

		return () => {
			cancelAnimationFrame(firstFrame);
			cancelAnimationFrame(secondFrame);
		};
	}, [zoom]);

	const runAtReadableZoom = React.useCallback(
		(action: () => void) => {
			if (isCompact && zoom < READABLE_ZOOM) {
				afterZoomRef.current = { zoom: READABLE_ZOOM, run: action };
				setZoom(READABLE_ZOOM);
				return;
			}

			action();
		},
		[isCompact, zoom, setZoom],
	);

	const isShownInViewer = React.useCallback(
		(fields: readonly ViewerFieldInfo[], field: DocumentFieldView): boolean => {
			if (fields.length === 0) {
				return true;
			}

			const placement = fieldIndex.findPlacement(field.key);

			return (
				placement !== null &&
				findViewerOccurrence(fields, placement.uuid, placement.positions) !== null
			);
		},
		[fieldIndex],
	);

	const currentFieldKey = panelTarget?.type === 'field' ? panelTarget.fieldKey : null;

	// The fields the viewer reports, read again each time the revision moves (the viewer's state is only
	// reachable through its handle).
	const viewerFields = React.useMemo(
		() => ({ revision: viewerRevision, fields: handleRef.current?.fields() ?? [] }),
		[viewerRevision],
	);

	const nextPendingField = React.useMemo(
		() =>
			findAdjacentPendingField(document.orderedFields(), currentFieldKey, 'next', (field) =>
				isShownInViewer(viewerFields.fields, field),
			),
		[document, currentFieldKey, isShownInViewer, viewerFields],
	);

	const goToField = React.useCallback(
		(fieldKey: string) => {
			if (fieldIndex.findPlacement(fieldKey) === null) {
				return;
			}

			revealRequestRef.current = fieldKey;
			requestDocumentPanel({ type: 'field', fieldKey, occurrenceKey: null });
		},
		[fieldIndex],
	);

	const goToAdjacentPending = React.useCallback(
		(direction: 'next' | 'previous') => {
			if (editingFieldSignal.peek()?.isDirty) {
				setPendingDiscard({ type: 'step', direction });
				return;
			}

			const open = documentPanelSignal.peek();
			const fields = handleRef.current?.fields() ?? [];
			const adjacent = findAdjacentPendingField(
				document.orderedFields(),
				open?.type === 'field' ? open.fieldKey : null,
				direction,
				(field) => isShownInViewer(fields, field),
			);

			if (adjacent !== null) {
				goToField(adjacent.key);
			}
		},
		[document, isShownInViewer, goToField],
	);

	const revealItem = React.useCallback(
		(listKey: string, index: number) => {
			const handle = handleRef.current;
			const list = document.findListByKey(listKey);
			const placement = fieldIndex.findListPlacement(listKey);

			if (handle === null || list === null || placement === null) {
				return;
			}

			const itemPath = findViewerItemPath(handle.fields(), list, placement.positions, index);

			if (itemPath !== null) {
				runAtReadableZoom(() => {
					handle.reveal({ item: itemPath });
				});
			}
		},
		[document, fieldIndex, runAtReadableZoom],
	);

	const revealChange = React.useCallback(
		(change: ShownChange) => {
			const handle = handleRef.current;

			if (handle === null) {
				return;
			}

			const positions = parseInstancePositions(change.instancePath ?? undefined);
			const occurrence = findViewerOccurrence(handle.fields(), change.fieldId, positions);

			if (occurrence !== null) {
				runAtReadableZoom(() => {
					handle.reveal({ field: change.fieldId, occurrence: occurrence.index });
				});
			}
		},
		[runAtReadableZoom],
	);

	// Keeps the viewer's selection in step with the open panel, and reveals the field when the panel was
	// opened by a navigation (`goToField`) rather than by a click on the document itself.
	React.useEffect(() => {
		const handle = handleRef.current;

		if (handle === null) {
			return;
		}

		if (panelTarget === null || panelTarget.type !== 'field') {
			handle.clearSelection();
			return;
		}

		const placement = fieldIndex.findPlacement(panelTarget.fieldKey);
		const occurrence =
			placement === null
				? null
				: findViewerOccurrence(viewerFields.fields, placement.uuid, placement.positions);

		if (placement === null || occurrence === null) {
			handle.clearSelection();
			return;
		}

		handle.select({ field: placement.uuid, occurrence: occurrence.index });

		if (revealRequestRef.current === panelTarget.fieldKey) {
			revealRequestRef.current = null;
			runAtReadableZoom(() => {
				handle.reveal({ field: placement.uuid, occurrence: occurrence.index });
			});
		}
	}, [panelTarget, fieldIndex, viewerFields, runAtReadableZoom]);

	// Gives the focus back to what opened the sidebar once it closes.
	const wasOpenRef = React.useRef(false);
	React.useEffect(() => {
		if (panelTarget !== null) {
			wasOpenRef.current = true;
			return;
		}

		if (!wasOpenRef.current) {
			return;
		}

		wasOpenRef.current = false;
		const opener = takeDocumentPanelOpener();

		if (opener?.isConnected) {
			opener.focus({ preventScroll: true });
		}
	}, [panelTarget]);

	const handleClick = React.useCallback(
		(event: ViewerClickEvent) => {
			const target = resolvePanelTarget(event, fieldIndex);

			if (target === null || isSamePanelTarget(documentPanelSignal.peek(), target)) {
				return;
			}

			revealRequestRef.current = null;
			requestDocumentPanel(target);
		},
		[fieldIndex],
	);

	const handleEditItem = React.useCallback(
		(event: ViewerEditItemEvent) => {
			const target = fieldIndex.resolveListTarget(event.list.id, event.path, true);

			if (target !== null) {
				revealRequestRef.current = null;
				requestDocumentPanel({ type: 'list-item', listKey: target.list.key, index: event.index });
			}
		},
		[fieldIndex],
	);

	const handleAddItem = React.useCallback(
		(event: ViewerAddItemEvent) => {
			const target = fieldIndex.resolveListTarget(event.list.id, event.path, false);

			if (target === null) {
				return;
			}

			const position =
				event.position.kind === 'after' ? event.position.index + 1 : target.list.items.length;
			listActionHandlerRef.current?.({ kind: 'add', listKey: target.list.key, position });
		},
		[fieldIndex],
	);

	const handleRemoveItem = React.useCallback(
		(event: ViewerRemoveItemEvent) => {
			const target = fieldIndex.resolveListTarget(event.list.id, event.path, true);

			if (target !== null) {
				listActionHandlerRef.current?.({
					kind: 'remove',
					listKey: target.list.key,
					index: event.index,
				});
			}
		},
		[fieldIndex],
	);

	const handleMoveItem = React.useCallback(
		(event: ViewerMoveItemEvent) => {
			const target = fieldIndex.resolveListTarget(event.list.id, event.path, true);

			if (target !== null) {
				listActionHandlerRef.current?.({
					kind: 'move',
					listKey: target.list.key,
					from: event.from,
					to: event.to,
				});
			}
		},
		[fieldIndex],
	);

	const viewerHandlers = React.useMemo(
		() => ({
			onClick: handleClick,
			onAddItem: handleAddItem,
			onEditItem: handleEditItem,
			onRemoveItem: handleRemoveItem,
			onMoveItem: handleMoveItem,
		}),
		[handleClick, handleAddItem, handleEditItem, handleRemoveItem, handleMoveItem],
	);

	const value = React.useMemo<DocumentViewerContextValue>(
		() => ({
			handleRef,
			zoom,
			minZoom: MIN_ZOOM,
			maxZoom,
			setZoom,
			fitToWidth,
			goToField,
			goToAdjacentPending,
			nextPendingField,
			isFitted,
			fieldIndex,
			isViewerReady,
			isTemplateUnreadable,
			listActionHandlerRef,
			revealItem,
			revealChange,
			viewerHandlers,
			reportViewportWidth,
			reportViewerReady,
		}),
		[
			zoom,
			maxZoom,
			setZoom,
			fitToWidth,
			goToField,
			goToAdjacentPending,
			nextPendingField,
			isFitted,
			fieldIndex,
			isViewerReady,
			isTemplateUnreadable,
			revealItem,
			revealChange,
			viewerHandlers,
			reportViewportWidth,
			reportViewerReady,
		],
	);

	return <DocumentViewerContext.Provider value={value}>{children}</DocumentViewerContext.Provider>;
};

export default DocumentViewerProvider;
